import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { ApiError } from '@cbhq/data-layer';
import { useClearWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';

import { useOrganization } from '../../contexts/OrganizationContext';

import { BasicErrorFallback } from './BasicErrorFallback';
import { InAppErrorMessageFallback } from './InAppErrorMessageFallback';
import { teamAccessMessages } from './messages';

type Props = {
  error?: Error;
  resetErrorBoundary?: () => void;
};

export function TeamErrorFallback({ error, resetErrorBoundary }: Props) {
  const { formatMessage } = useIntl();
  const { selectedOrganization } = useOrganization();
  const clearWorkspaceMembers = useClearWorkspaceMembers({
    organizationId: selectedOrganization?.organizationId ?? '',
  });

  // When the user navigates away from a failed org (boundary remounts on key
  // change), clear the cached error so coming back triggers a fresh fetch
  // instead of immediately re-throwing the stale cached error.
  useEffect(() => () => clearWorkspaceMembers(), [clearWorkspaceMembers]);

  const retry = useCallback(() => {
    clearWorkspaceMembers();
    resetErrorBoundary?.();
  }, [clearWorkspaceMembers, resetErrorBoundary]);

  if (error instanceof ApiError && error.status === 403) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(teamAccessMessages.deniedTitle)}
        description={formatMessage(teamAccessMessages.deniedDescription)}
        error={error}
      />
    );
  }

  return <BasicErrorFallback error={error} retry={retry} />;
}
